import { action, makeAutoObservable } from "mobx";
import {
  sitchEventDetails,
  sitchEventListAttendees,
  sitchEventAttendeesSummary,
  sitchEventStartNewRound,
  sitchEventTaskStatus,
} from "../../../../../api/sitch_events";
import { components } from "../../../../../api/spec";
import CommunityNavigationStore from "../../navigation/communityNavigationStore";

// Helper function to delay execution
const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export class EventStore {
  eventCode = "";

  isLoading = false;

  error: string | null = null;

  sessionGuid: string | null = null;

  sessionData:
    | components["schemas"]["CommunityMatchmakingSessionSchema"]
    | null = null;

  sitchEventData: components["schemas"]["SitchEventDetailSchema"] | null = null;

  attendeesData: components["schemas"]["ListEventAttendeesResponse"] | null =
    null;

  attendeesSummary:
    | components["schemas"]["EventAttendeesSummaryResponse"]
    | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  clearSession(eventGuid?: string) {
    this.eventCode = "";
    this.error = null;
    this.sessionData = null;
    this.sessionGuid = null;
    this.sitchEventData = null;
    this.attendeesSummary = null;

    if (eventGuid) {
      CommunityNavigationStore.clearEventAuthentication(eventGuid);
    }
  }

  @action
  async loadEventData(eventGuid: string) {
    this.isLoading = true;
    this.error = null;
    try {
      this.sitchEventData = await sitchEventDetails(eventGuid);
      this.attendeesSummary = await sitchEventAttendeesSummary(eventGuid);
      this.attendeesData = await sitchEventListAttendees(eventGuid);
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to load event data";
    } finally {
      this.isLoading = false;
    }
  }

  async generateSetups() {
    this.isLoading = true;
    try {
      if (!this.sitchEventData?.guid) {
        throw new Error("Cannot generate setups without an event GUID.");
      }

      const startRoundResponse = await sitchEventStartNewRound({
        event: this.sitchEventData.guid,
      });

      if (!startRoundResponse.task_id) {
        throw new Error("Failed to get task ID for generating setups.");
      }

      // Poll for task completion
      let taskComplete = false;
      while (!taskComplete) {
        // eslint-disable-next-line no-await-in-loop
        const statusResponse = await sitchEventTaskStatus(
          startRoundResponse.task_id,
        );
        if (statusResponse.ready) {
          taskComplete = true;
        } else {
          // eslint-disable-next-line no-await-in-loop
          await sleep(2000); // Wait for 2 seconds before checking again
        }
      }

      return startRoundResponse;
    } catch (err) {
      console.error("Failed to generate setups:", err);
      throw err;
    } finally {
      this.isLoading = false;
    }
  }

  get profileCompletionPercentage() {
    if (!this.attendeesSummary) return 0;

    if (this.attendeesSummary.num_attendees_total === 0) return 0;
    return Math.round(
      ((this.attendeesSummary.num_profiles_completed || 0) /
        (this.attendeesSummary.num_attendees_total || 1)) *
        100,
    );
  }

  async sendInviteLink() {
    try {
      const eventName = this.sitchEventData?.name || "our event";
      const shareText = `👀 You’re invited to ${eventName}! Fill out your profile at joinsitch.com/app so we can set you up! Don’t forget: your exclusive event code is ${this.sitchEventData?.referral_code}`;

      if (navigator.share) {
        await navigator.share({
          title: `Join ${eventName}!`,
          text: shareText,
        });
        return;
      }
    } catch (error) {
      this.error = "Failed to share invite link";
    }
  }

  get isSessionActive() {
    return Boolean(this.sessionGuid && this.sessionData);
  }
}

export default new EventStore();
